import { useState } from 'react';
import { patchTrip } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { UsersThree, Plus, Trash, CaretRight, Shuffle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { generateRoundDraw } from '../golf';

const PRESET_COLORS = ['#1B4332', '#1565C0', '#D32F2F', '#F9A825', '#7B1FA2', '#00695C', '#E65100', '#37474F'];

export default function TripTeamsSection({ trip, code, isOrganiser, onUpdate }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editTeams, setEditTeams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openEditor = () => {
    if (trip.trip_teams?.length > 0) {
      setEditTeams(trip.trip_teams.map(t => ({ ...t, player_ids: [...t.player_ids] })));
    } else {
      setEditTeams([
        { id: crypto.randomUUID(), name: 'Team A', color: '#1B4332', player_ids: [] },
        { id: crypto.randomUUID(), name: 'Team B', color: '#1565C0', player_ids: [] },
      ]);
    }
    setError('');
    setEditOpen(true);
  };

  const addTeam = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const idx = editTeams.length;
    const color = PRESET_COLORS[idx % PRESET_COLORS.length];
    setEditTeams(prev => [...prev, {
      id: crypto.randomUUID(),
      name: `Team ${letters[idx] || idx + 1}`,
      color,
      player_ids: [],
    }]);
  };

  const removeTeam = (idx) => setEditTeams(prev => prev.filter((_, i) => i !== idx));
  const setTeamName = (idx, name) => setEditTeams(prev => prev.map((t, i) => i === idx ? { ...t, name } : t));
  const setTeamColor = (idx, color) => setEditTeams(prev => prev.map((t, i) => i === idx ? { ...t, color } : t));

  const handleRandomise = () => {
    const hasAssignments = editTeams.some(t => t.player_ids.length > 0);
    if (hasAssignments && !window.confirm('This will replace all current team assignments. Continue?')) return;
    const active = (trip.players || []).filter(p => p.active !== false);
    const pids = active.map(p => p.id);
    const playerMap = Object.fromEntries(active.map(p => [p.id, p]));
    const teamCount = editTeams.length;
    const teamSize = Math.ceil(pids.length / teamCount);
    const orderFn = (id) => {
      const p = playerMap[id];
      if (!p) return 99;
      return p.handicap_mode === 'ph' && p.manual_ph != null ? p.manual_ph : p.whs_index;
    };
    const split = generateRoundDraw(pids, orderFn, teamSize, [], 400);
    setEditTeams(prev => prev.map((t, i) => ({ ...t, player_ids: split[i] || [] })));
  };

  const assignPlayer = (playerId, teamId) => {
    setEditTeams(prev => prev.map(t => ({
      ...t,
      player_ids: t.id === teamId
        ? (t.player_ids.includes(playerId) ? t.player_ids : [...t.player_ids, playerId])
        : t.player_ids.filter(p => p !== playerId),
    })));
  };

  const unassignPlayer = (playerId) => {
    setEditTeams(prev => prev.map(t => ({ ...t, player_ids: t.player_ids.filter(p => p !== playerId) })));
  };

  const clearAllTeams = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await patchTrip(code, { trip_teams: [] });
      onUpdate(res.data);
      setEditOpen(false);
      toast.success('Trip teams cleared');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to clear');
    } finally { setSaving(false); }
  };

  const saveTeams = async () => {
    for (const t of editTeams) {
      if (!t.name.trim()) { setError('All teams need a name'); return; }
    }
    const names = editTeams.map(t => t.name.trim().toLowerCase());
    if (new Set(names).size !== names.length) { setError('Team names must be unique'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await patchTrip(code, { trip_teams: editTeams });
      onUpdate(res.data);
      setEditOpen(false);
      toast.success('Trip teams saved');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to save';
      const errorMap = {
        duplicate_team_name: 'Team names must be unique',
        empty_team_name: 'All teams need a name',
        invalid_team_color: 'Invalid team colour',
        unknown_player_id: 'Unknown player in assignment',
        player_in_multiple_teams: 'A player is assigned to multiple teams',
        organiser_required: 'Organiser access required',
      };
      setError(errorMap[detail] || detail);
    } finally { setSaving(false); }
  };

  const activePlayers = (trip.players || []).filter(p => p.active !== false);
  const hasTeams = trip.trip_teams?.length > 0;

  return (
    <>
      {hasTeams && (
        <div className="px-4 pt-6 max-w-lg mx-auto" data-testid="trip-teams-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1D242B]">Trip Teams</h2>
            {isOrganiser && (
              <Button onClick={openEditor} size="sm" variant="outline" className="rounded-xl h-9 px-3 text-sm" data-testid="edit-trip-teams-btn">Edit</Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {trip.trip_teams.map(team => (
              <Card key={team.id} className="p-3 bg-white border-[#E1E6E4] rounded-xl overflow-hidden"
                style={{ borderLeftColor: team.color, borderLeftWidth: 4 }} data-testid={`trip-team-card-${team.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: team.color }} />
                  <span className="font-bold text-sm text-[#1D242B] truncate">{team.name}</span>
                  <Badge className="text-[10px] bg-[#F4F6F5] text-[#455561] flex-shrink-0">{team.player_ids.length}</Badge>
                </div>
                <div className="space-y-0.5">
                  {team.player_ids.map(pid => {
                    const p = trip.players.find(pl => pl.id === pid);
                    return p ? <span key={pid} className="text-xs text-[#455561] block truncate">{p.name}</span> : null;
                  })}
                  {team.player_ids.length === 0 && <span className="text-xs text-[#B8C7C0] italic">No players</span>}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {!hasTeams && isOrganiser && activePlayers.length >= 2 && (
        <div className="px-4 pt-4 max-w-lg mx-auto" data-testid="trip-teams-setup">
          <Button onClick={openEditor} variant="outline"
            className="w-full h-14 rounded-xl border-[#E1E6E4] text-[#1D242B] justify-between text-base" data-testid="setup-trip-teams-btn">
            <span className="flex items-center gap-2"><UsersThree className="w-5 h-5 text-[#1B4332]" weight="fill" /> Set Up Trip Teams</span>
            <CaretRight className="w-5 h-5 text-[#B8C7C0]" />
          </Button>
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={open => { if (!open) setEditOpen(false); }}>
        <DialogContent className="sm:max-w-md rounded-2xl max-h-[85vh] flex flex-col" data-testid="edit-trip-teams-dialog">
          <DialogHeader><DialogTitle>Edit Trip Teams</DialogTitle></DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            {editTeams.map((team, ti) => (
              <div key={team.id} className="p-3 bg-[#F4F6F5] rounded-xl space-y-2" data-testid={`edit-team-${ti}`}>
                <div className="flex items-center gap-2">
                  <Input value={team.name} onChange={e => setTeamName(ti, e.target.value)}
                    className="h-9 rounded-lg text-sm flex-1" placeholder="Team name" data-testid={`team-name-${ti}`} />
                  {editTeams.length > 2 && (
                    <button onClick={() => removeTeam(ti)} className="text-[#D32F2F] hover:bg-red-50 p-1.5 rounded" data-testid={`remove-team-${ti}`}>
                      <Trash className="w-4 h-4" weight="bold" />
                    </button>
                  )}
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => setTeamColor(ti, c)}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${team.color === c ? 'border-[#1D242B] scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} data-testid={`color-${ti}-${c.slice(1)}`} />
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Button onClick={addTeam} variant="outline" size="sm" className="rounded-xl" data-testid="add-team-btn">
                <Plus className="w-4 h-4 mr-1" weight="bold" /> Add Team
              </Button>
              <Button onClick={handleRandomise} variant="outline" size="sm" className="rounded-xl"
                disabled={editTeams.length < 2 || activePlayers.length < editTeams.length}
                data-testid="randomise-teams-btn">
                <Shuffle className="w-4 h-4 mr-1" weight="bold" /> Randomise
              </Button>
            </div>
            <p className="text-[10px] text-[#B8C7C0] -mt-2">Balances by WHS handicap. Existing assignments will be replaced.</p>

            {activePlayers.length > 0 && (
              <div className="pt-2 border-t border-[#E1E6E4]">
                <p className="text-xs font-semibold text-[#455561] mb-3">Assign Players</p>
                <div className="space-y-2">
                  {activePlayers.map(player => {
                    const currentTeamId = editTeams.find(t => t.player_ids.includes(player.id))?.id || null;
                    return (
                      <div key={player.id} className="flex items-center gap-2" data-testid={`assign-row-${player.id}`}>
                        <span className="text-sm text-[#1D242B] flex-1 min-w-0 truncate">{player.name}</span>
                        <div className="flex gap-1 flex-shrink-0">
                          {editTeams.map(t => (
                            <button key={t.id} onClick={() => assignPlayer(player.id, t.id)}
                              className={`w-7 h-7 rounded-full border-2 text-[10px] font-bold text-white transition-all ${currentTeamId === t.id ? 'border-[#1D242B] ring-1 ring-[#1D242B] scale-110' : 'border-transparent opacity-40'}`}
                              style={{ backgroundColor: t.color }} title={t.name} data-testid={`assign-${player.id}-${t.id}`}>
                              {t.name.charAt(0).toUpperCase()}
                            </button>
                          ))}
                          <button onClick={() => unassignPlayer(player.id)}
                            className={`w-7 h-7 rounded-full border-2 text-[10px] font-bold transition-all ${!currentTeamId ? 'border-[#1D242B] bg-[#E1E6E4] text-[#1D242B] scale-110' : 'border-transparent bg-[#F4F6F5] text-[#B8C7C0] opacity-40'}`}
                            title="Unassigned" data-testid={`unassign-${player.id}`}>—</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <p className="text-xs text-[#D32F2F]" data-testid="team-edit-error">{error}</p>}
          </div>
          <DialogFooter className="flex gap-2 mt-2 pt-2 border-t border-[#E1E6E4]">
            {hasTeams && (
              <Button variant="ghost" onClick={clearAllTeams} disabled={saving}
                className="text-[#D32F2F] hover:bg-red-50 text-sm" data-testid="clear-teams-btn">Clear All</Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-xl" data-testid="cancel-teams-btn">Cancel</Button>
            <Button onClick={saveTeams} disabled={saving || editTeams.length < 2}
              className="bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl" data-testid="save-teams-btn">
              {saving ? 'Saving...' : 'Save Teams'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
